import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, where, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface ReportData {
  date: string;
  venueId: string;
  venueName: string;
  revenue: number;
  matches: number;
  sessions: number;
  averageRevenue: number;
}

const RevenueReports: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isVenueOperator, venueIds: currentUserVenueIds, user } = useAuth();
  const [reports, setReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  const [venues, setVenues] = useState<{ id: string; name: string; pricePerHour: number }[]>([]);
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [reportType, setReportType] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    loadReports();
  }, [selectedVenue, startDate, endDate, reportType]);

  const loadVenues = async () => {
    try {
      let venuesData: { id: string; name: string; pricePerHour: number }[] = [];

      if (isVenueOperator && !isAdmin) {
        // For venue operators, load only their venues
        if (user) {
          const venuesQuery = query(
            collection(db, 'venues'),
            where('operatorId', '==', user.uid)
          );
          const venuesSnapshot = await getDocs(venuesQuery);
          venuesData = venuesSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            pricePerHour: doc.data().pricePerHour || 2500
          }));
        }
      } else {
        // For admins, load all venues
        const venuesSnapshot = await getDocs(collection(db, 'venues'));
        venuesData = venuesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          pricePerHour: doc.data().pricePerHour || 2500
        }));
      }

      setVenues(venuesData);
    } catch (error) {
      console.error('Error loading venues:', error);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);

      const bookingsRef = collection(db, 'bookings');
      const start = Timestamp.fromDate(new Date(startDate));
      const end = Timestamp.fromDate(new Date(endDate + 'T23:59:59'));

      let bookingsQuery;

      if (selectedVenue !== 'all') {
        // Specific venue selected
        bookingsQuery = query(
          bookingsRef,
          where('venueId', '==', selectedVenue),
          where('startTime', '>=', start),
          where('startTime', '<=', end),
          where('status', 'in', ['confirmed', 'completed'])
        );
      } else {
        // All venues - need to filter by operator's venues if venue operator
        if (isVenueOperator && !isAdmin && venues.length > 0) {
          // For venue operators, only get bookings from their venues
          const venueIds = venues.map(v => v.id);
          // Firestore 'in' query supports max 10 items, so we need to batch
          const batchSize = 10;
          const allBookings: any[] = [];

          for (let i = 0; i < venueIds.length; i += batchSize) {
            const batchVenueIds = venueIds.slice(i, i + batchSize);
            const batchQuery = query(
              bookingsRef,
              where('venueId', 'in', batchVenueIds),
              where('startTime', '>=', start),
              where('startTime', '<=', end),
              where('status', 'in', ['confirmed', 'completed'])
            );
            const batchSnapshot = await getDocs(batchQuery);
            allBookings.push(...batchSnapshot.docs);
          }

          // Create a fake snapshot object
          const bookingsSnapshot = {
            docs: allBookings
          };

          await processBookings(bookingsSnapshot);
          return;
        } else {
          // For admins, get all bookings
          bookingsQuery = query(
            bookingsRef,
            where('startTime', '>=', start),
            where('startTime', '<=', end),
            where('status', 'in', ['confirmed', 'completed'])
          );
        }
      }

      const bookingsSnapshot = await getDocs(bookingsQuery);
      await processBookings(bookingsSnapshot);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const processBookings = async (bookingsSnapshot: any) => {
    // Load venue data for price calculation
    const venueDataMap: { [venueId: string]: { name: string; pricePerHour: number } } = {};
    for (const venue of venues) {
      venueDataMap[venue.id] = {
        name: venue.name,
        pricePerHour: venue.pricePerHour || 2500
      };
    }

    const reportsMap: { [key: string]: ReportData } = {};

    for (const bookingDoc of bookingsSnapshot.docs) {
      const booking = bookingDoc.data();
      const bookingDate = booking.startTime.toDate();

      // Calculate hours
      const startTime = booking.startTime.toDate();
      const endTime = booking.endTime.toDate();
      const hours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

      // Get venue data
      let venueName = 'Unknown Venue';
      let pricePerHour = 2500;

      if (venueDataMap[booking.venueId]) {
        venueName = venueDataMap[booking.venueId].name;
        pricePerHour = venueDataMap[booking.venueId].pricePerHour;
      } else {
        // Fetch venue data if not in map
        try {
          const venueDoc = await getDoc(doc(db, 'venues', booking.venueId));
          if (venueDoc.exists()) {
            venueName = venueDoc.data().name;
            pricePerHour = venueDoc.data().pricePerHour || 2500;
            venueDataMap[booking.venueId] = { name: venueName, pricePerHour };
          }
        } catch (err) {
          console.error('Error fetching venue:', err);
        }
      }

      const revenue = hours * pricePerHour;

      let key: string;

      if (reportType === 'daily') {
        key = bookingDate.toISOString().split('T')[0];
      } else if (reportType === 'weekly') {
        const weekStart = new Date(bookingDate);
        weekStart.setDate(bookingDate.getDate() - bookingDate.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${bookingDate.getFullYear()}-${String(bookingDate.getMonth() + 1).padStart(2, '0')}`;
      }

      // If "all venues" is selected, aggregate by date only
      // If specific venue is selected, aggregate by date + venue
      const aggregationKey = selectedVenue === 'all' ? key : `${key}_${booking.venueId}`;

      if (!reportsMap[aggregationKey]) {
        reportsMap[aggregationKey] = {
          date: key,
          venueId: selectedVenue === 'all' ? 'all' : booking.venueId,
          venueName: selectedVenue === 'all' ? 'Összes helyszín' : venueName,
          revenue: 0,
          matches: 0,
          sessions: 0,
          averageRevenue: 0
        };
      }

      reportsMap[aggregationKey].revenue += revenue;
      reportsMap[aggregationKey].matches += 1; // Each booking is a match/session
      reportsMap[aggregationKey].sessions += 1;
    }

    const reportsData = Object.values(reportsMap).map(report => ({
      ...report,
      averageRevenue: report.sessions > 0 ? report.revenue / report.sessions : 0
    })).sort((a, b) => b.date.localeCompare(a.date));

    setReports(reportsData);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Venue', 'Revenue', 'Matches', 'Average Revenue'];
    const csvContent = [
      headers.join(','),
      ...reports.map(report =>
        [
          report.date,
          report.venueName,
          report.revenue.toFixed(2),
          report.matches,
          report.averageRevenue.toFixed(2)
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `revenue_report_${startDate}_to_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Revenue Report</title>');
      printWindow.document.write('<style>');
      printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; }');
      printWindow.document.write('h1 { color: #333; }');
      printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; }');
      printWindow.document.write('th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }');
      printWindow.document.write('th { background-color: #4CAF50; color: white; }');
      printWindow.document.write('tr:nth-child(even) { background-color: #f2f2f2; }');
      printWindow.document.write('.summary { margin: 20px 0; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #4CAF50; }');
      printWindow.document.write('</style></head><body>');
      printWindow.document.write(`<h1>${t('revenueReports.title')}</h1>`);
      printWindow.document.write(`<p><strong>${t('revenueReports.period')}:</strong> ${startDate} - ${endDate}</p>`);

      const totalRevenue = reports.reduce((sum, r) => sum + r.revenue, 0);
      const totalMatches = reports.reduce((sum, r) => sum + r.matches, 0);
      const totalSessions = reports.reduce((sum, r) => sum + r.sessions, 0);

      printWindow.document.write('<div class="summary">');
      printWindow.document.write(`<p><strong>${t('revenueReports.totalRevenue')}:</strong> ${totalRevenue.toFixed(0)} Ft</p>`);
      printWindow.document.write(`<p><strong>${t('revenueReports.totalMatches')}:</strong> ${totalMatches}</p>`);
      printWindow.document.write('</div>');

      printWindow.document.write('<table>');
      printWindow.document.write('<thead><tr>');
      printWindow.document.write(`<th>${t('revenueReports.date')}</th>`);
      printWindow.document.write(`<th>${t('revenueReports.venue')}</th>`);
      printWindow.document.write(`<th>${t('revenueReports.revenue')}</th>`);
      printWindow.document.write(`<th>${t('revenueReports.matches')}</th>`);
      printWindow.document.write(`<th>${t('revenueReports.avgRevenue')}</th>`);
      printWindow.document.write('</tr></thead><tbody>');

      reports.forEach(report => {
        printWindow.document.write('<tr>');
        printWindow.document.write(`<td>${report.date}</td>`);
        printWindow.document.write(`<td>${report.venueName}</td>`);
        printWindow.document.write(`<td>${report.revenue.toFixed(0)} Ft</td>`);
        printWindow.document.write(`<td>${report.matches}</td>`);
        printWindow.document.write(`<td>${report.averageRevenue.toFixed(0)} Ft</td>`);
        printWindow.document.write('</tr>');
      });

      printWindow.document.write('</tbody></table>');
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  const totalRevenue = reports.reduce((sum, r) => sum + r.revenue, 0);
  const totalMatches = reports.reduce((sum, r) => sum + r.matches, 0);
  const totalSessions = reports.reduce((sum, r) => sum + r.sessions, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#00AFF2] mx-auto mb-4"></div>
          <p className="text-[#B8B8C3]">{t('revenueReports.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      <div className="card-dark p-6">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gradient-cyan mb-2 whitespace-nowrap">{t('revenueReports.title')}</h2>
              <p className="text-[#B8B8C3]">{t('revenueReports.description')}</p>
            </div>
            <div className="hidden sm:flex gap-3">
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-gradient-to-r from-[#2bc48a] to-[#00AFF2] text-[#010306] rounded-lg hover:shadow-[0_0_20px_rgba(43,196,138,0.5)] transition-all font-semibold flex items-center gap-2 whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('revenueReports.exportCSV')}
              </button>
              <button
                onClick={exportToPDF}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:shadow-[0_0_20px_rgba(220,38,38,0.5)] transition-all font-semibold flex items-center gap-2 whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {t('revenueReports.exportPDF')}
              </button>
            </div>
          </div>

          <div className="sm:hidden flex gap-2 mb-4">
            <button
              onClick={exportToCSV}
              className="flex-1 px-3 py-2 bg-gradient-to-r from-[#2bc48a] to-[#00AFF2] text-[#010306] rounded-lg hover:shadow-[0_0_20px_rgba(43,196,138,0.5)] transition-all font-semibold flex items-center justify-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t('revenueReports.exportCSV')}
            </button>
            <button
              onClick={exportToPDF}
              className="flex-1 px-3 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:shadow-[0_0_20px_rgba(220,38,38,0.5)] transition-all font-semibold flex items-center justify-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {t('revenueReports.exportPDF')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
              {t('revenueReports.venue')}
            </label>
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="input-dark w-full"
            >
              <option value="all">{t('revenueReports.allVenues')}</option>
              {venues.map(venue => (
                <option key={venue.id} value={venue.id}>{venue.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
              {t('revenueReports.reportType')}
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value as any)}
              className="input-dark w-full"
            >
              <option value="daily">{t('revenueReports.daily')}</option>
              <option value="weekly">{t('revenueReports.weekly')}</option>
              <option value="monthly">{t('revenueReports.monthly')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
              {t('revenueReports.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max="9999-12-31"
              className="input-dark w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
              {t('revenueReports.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              max="9999-12-31"
              className="input-dark w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#00AFF2]/10 rounded-lg trophy-icon">
                <svg className="w-8 h-8 text-[#00AFF2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="stat-label">{t('revenueReports.totalRevenue')}</p>
            <p className="stat-number">{totalRevenue.toFixed(0)} Ft</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-[#2bc48a]/10 rounded-lg">
                <svg className="w-8 h-8 text-[#2bc48a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="stat-label">{t('revenueReports.totalMatches')}</p>
            <p className="stat-number">{totalMatches}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#31383D]">
            <thead className="bg-[#1d232a]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('revenueReports.date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('revenueReports.venue')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('revenueReports.revenue')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('revenueReports.matches')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B8B8C3] uppercase tracking-wider">
                  {t('revenueReports.avgRevenue')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#15191E] divide-y divide-[#31383D]">
              {reports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-[#8a8f99]">
                    {t('revenueReports.noData')}
                  </td>
                </tr>
              ) : (
                reports.map((report, index) => (
                  <tr key={index} className="hover:bg-[#1d232a] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{report.date}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#B8B8C3]">{report.venueName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-[#2bc48a]">{report.revenue.toFixed(0)} Ft</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#B8B8C3]">
                      {report.matches}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">{report.averageRevenue.toFixed(0)} Ft</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RevenueReports;
